import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { aiAPI } from '../services/api'
import AIChatSettings from './AIChatSettings'
import WaveformVisualizer from './WaveformVisualizer'
import { useSpeechRecognition } from '../hooks/useSpeechRecognition'
import { useAudioPlayback } from '../hooks/useAudioPlayback'
import { AIActionHandler, ConfirmationCallback, SearchResult, ActionButton, generateSearchActionButtons } from '../services/aiActionHandler'
import toast from 'react-hot-toast'
import { Menu, Plus, Settings, X, Bot, MessageSquare, ChevronRight, Trash2, Mic, MicOff, Search, BookOpen, Square, AlertTriangle } from 'lucide-react'

// Enhanced formatted message component for rendering AI responses with proper markdown styling
const FormattedMessage = ({ content }: { content: string }) => {
  if (!content) return null

  // Split content into lines for processing
  const lines = content.split('\n')

  return (
    <div className="formatted-message leading-relaxed text-neutral-100 space-y-2">
      {lines.map((line, idx) => {
        const trimmedLine = line.trim()

        // Skip empty lines
        if (!trimmedLine) return <div key={idx} className="h-2" />

        // Handle headers (## or ###)
        if (trimmedLine.startsWith('### ')) {
          return <h4 key={idx} className="text-purple-300 font-semibold text-sm mt-3 mb-1">{formatInlineMarkdown(trimmedLine.slice(4))}</h4>
        }
        if (trimmedLine.startsWith('## ')) {
          return <h3 key={idx} className="text-purple-200 font-bold text-base mt-4 mb-2">{formatInlineMarkdown(trimmedLine.slice(3))}</h3>
        }
        if (trimmedLine.startsWith('# ')) {
          return <h2 key={idx} className="text-purple-100 font-bold text-lg mt-4 mb-2">{formatInlineMarkdown(trimmedLine.slice(2))}</h2>
        }

        // Handle code blocks (```code```)
        if (trimmedLine.startsWith('```')) {
          return null // Skip code block markers (handled separately if needed)
        }

        // Handle bullet points (• or - or *)
        if (trimmedLine.match(/^[•\-\*]\s/)) {
          const bulletContent = trimmedLine.replace(/^[•\-\*]\s*/, '')
          return (
            <div key={idx} className="flex items-start gap-2 ml-2">
              <span className="text-purple-400 mt-0.5">▸</span>
              <span>{formatInlineMarkdown(bulletContent)}</span>
            </div>
          )
        }

        // Handle numbered lists (1. 2. 3.)
        const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/)
        if (numberedMatch) {
          return (
            <div key={idx} className="flex items-start gap-2 ml-2">
              <span className="text-purple-400 font-semibold min-w-[20px]">{numberedMatch[1]}.</span>
              <span>{formatInlineMarkdown(numberedMatch[2])}</span>
            </div>
          )
        }

        // Regular paragraph
        return <p key={idx} className="text-neutral-100">{formatInlineMarkdown(trimmedLine)}</p>
      })}
    </div>
  )
}

// Helper function to format inline markdown (bold, italic, code, links)
const formatInlineMarkdown = (text: string): React.ReactNode => {
  if (!text) return null

  // Process text into segments with different formatting
  const segments: React.ReactNode[] = []
  let remaining = text
  let keyIndex = 0

  while (remaining.length > 0) {
    // Find the next markdown pattern
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/)
    const italicMatch = remaining.match(/(?<!\*)\*([^*]+?)\*(?!\*)/)
    const codeMatch = remaining.match(/`([^`]+)`/)
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/)

    // Find which pattern comes first
    const patterns = [
      { type: 'bold', match: boldMatch, index: boldMatch?.index ?? Infinity },
      { type: 'italic', match: italicMatch, index: italicMatch?.index ?? Infinity },
      { type: 'code', match: codeMatch, index: codeMatch?.index ?? Infinity },
      { type: 'link', match: linkMatch, index: linkMatch?.index ?? Infinity },
    ].sort((a, b) => a.index - b.index)

    const first = patterns[0]

    if (first.index === Infinity || !first.match) {
      // No more patterns, push remaining text
      segments.push(remaining)
      break
    }

    // Push text before the pattern
    if (first.index > 0) {
      segments.push(remaining.slice(0, first.index))
    }

    // Handle the pattern
    switch (first.type) {
      case 'bold':
        segments.push(<strong key={`b${keyIndex++}`} className="font-semibold text-white">{first.match[1]}</strong>)
        remaining = remaining.slice(first.index + first.match[0].length)
        break
      case 'italic':
        segments.push(<em key={`i${keyIndex++}`} className="italic text-neutral-200">{first.match[1]}</em>)
        remaining = remaining.slice(first.index + first.match[0].length)
        break
      case 'code':
        segments.push(<code key={`c${keyIndex++}`} className="px-1.5 py-0.5 bg-neutral-700 rounded text-purple-300 text-sm font-mono">{first.match[1]}</code>)
        remaining = remaining.slice(first.index + first.match[0].length)
        break
      case 'link':
        segments.push(<a key={`l${keyIndex++}`} href={first.match[2]} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">{first.match[1]}</a>)
        remaining = remaining.slice(first.index + first.match[0].length)
        break
    }
  }

  return segments.length === 1 ? segments[0] : <>{segments}</>
}

interface Message {
  role: 'user' | 'ai'
  content: string
  isStreaming?: boolean
  searchResults?: SearchResult
  actionButtons?: ActionButton[]
}

interface Session {
  id: string
  title?: string
  session_type: string
  started_at: string
  status: string
}

export default function FloatingAIMentor() {
  const [isOpen, setIsOpen] = useState(false)
  const [isIdle, setIsIdle] = useState(true) // Start in idle mode (minimized)
  const [showSettings, setShowSettings] = useState(false)
  const [showConversations, setShowConversations] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [sessions, setSessions] = useState<Session[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(() => {
    // Get user-specific session ID to avoid sharing sessions between users
    const userData = sessionStorage.getItem('user') || localStorage.getItem('user')
    if (userData) {
      try {
        const user = JSON.parse(userData)
        return sessionStorage.getItem(`ai_mentor_session_id_${user.id}`) || localStorage.getItem(`ai_mentor_session_id_${user.id}`)
      } catch {
        return null
      }
    }
    return null
  })
  const [streamingText, setStreamingText] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [confirmationDialog, setConfirmationDialog] = useState<ConfirmationCallback | null>(null)
  const [pendingStreamText, setPendingStreamText] = useState('')
  const [isFirstMessage, setIsFirstMessage] = useState(true)
  const [usedSuggestions, setUsedSuggestions] = useState<Set<string>>(new Set())
  const [userRole, setUserRole] = useState<string>('student')
  // ReturnType<typeof setInterval>, not number: in the browser setInterval
  // returns a number, but under Node's types it returns a Timeout object. Both
  // are in scope now that vitest/jsdom types are part of the typecheck, and
  // hardcoding `number` broke `tsc` (and therefore `npm run build`) as soon as
  // they were. This form is correct under either lib.
  const streamingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const actionHandlerRef = useRef<AIActionHandler | null>(null)
  const shouldStopRef = useRef(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  // ── Voice Mode State ────────────────────────────────────────────────────
  // Voice is COMING SOON: the server decides, so the UI never offers a mode
  // that would 503 mid-conversation.
  const [voiceEnabled, setVoiceEnabled] = useState(false)
  const [isVoiceMode, setIsVoiceMode] = useState(false)
  const [voiceStatus, setVoiceStatus] = useState<'idle' | 'listening' | 'processing' | 'speaking'>('idle')
  const [voiceTranscript, setVoiceTranscript] = useState<Array<{ role: 'user' | 'ai'; text: string }>>([]
  )
  const [recordingTime, setRecordingTime] = useState(0)
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const transcriptEndRef = useRef<HTMLDivElement>(null)

  // Voice hooks
  const speech = useSpeechRecognition()
  const audio = useAudioPlayback()

  // Ref for sessionId to avoid stale closures
  const sessionIdRef = useRef(sessionId)
  useEffect(() => { sessionIdRef.current = sessionId }, [sessionId])

  // Ask the server whether voice is live yet. Any failure keeps it disabled,
  // so the button degrades to "coming soon" rather than erroring on click.
  //
  // Only call when signed in: this component mounts on public pages too, and a
  // logged-out request would 401 → the api interceptor would try to refresh,
  // fail, and redirect a visitor to /login. (See ConditionalAIMentor.)
  useEffect(() => {
    if (!isAuthenticated()) { setVoiceEnabled(false); return }
    let cancelled = false
    aiAPI.getVoiceStatus?.()
      .then((res: any) => { if (!cancelled) setVoiceEnabled(!!res?.data?.enabled) })
      .catch(() => { if (!cancelled) setVoiceEnabled(false) })
    return () => { cancelled = true }
  }, [location.pathname])

  // Helper: send voice transcript to AI
  const handleVoiceSend = useCallback(async () => {
    // Wait for recognition to finalize transcript
    await new Promise(r => setTimeout(r, 800))
    // Read directly from refs — bypasses React state timing
    const captured = speech.getTranscript()
    const text = (captured.transcript || captured.interim || '').trim()

    if (!text) {
      toast.error('No speech detected. Please try again.')
      setVoiceStatus('idle')
      return
    }

    setVoiceStatus('processing')
    setVoiceTranscript(prev => [...prev, { role: 'user', text }])

    try {
      let currentSessionId = sessionIdRef.current
      if (!currentSessionId) {
        await createNewConversation()
        // Wait a tick for state to update
        await new Promise(r => setTimeout(r, 100))
        currentSessionId = sessionIdRef.current
      }
      if (!currentSessionId) {
        toast.error('Failed to create conversation')
        setVoiceStatus('idle')
        return
      }

      const res = await aiAPI.voiceChat({
        transcript: text,
        session_id: currentSessionId,
        current_page: location.pathname,
      })
      const aiText = res.data.ai_text || "I'm here to help!"
      setVoiceTranscript(prev => [...prev, { role: 'ai', text: aiText }])
      setMessages(prev => [...prev, { role: 'user', content: text }, { role: 'ai', content: aiText }])

      if (res.data.action && actionHandlerRef.current) {
        await actionHandlerRef.current.handleAction(res.data.action, currentSessionId)
      }
      if (res.data.audio_base64) {
        setVoiceStatus('speaking')
        await audio.play(res.data.audio_base64)
        setVoiceStatus('idle')
      } else {
        setVoiceStatus('idle')
      }
    } catch (err) {
      console.error('Voice chat error:', err)
      toast.error('Voice chat failed. Please try again.')
      setVoiceStatus('idle')
    }
  }, [audio, location.pathname, speech.getTranscript])

  // Toggle voice: click to start listening, click again to stop & send
  const handleVoiceToggle = useCallback(() => {
    if (voiceStatus === 'processing' || voiceStatus === 'speaking') return

    if (voiceStatus === 'listening') {
      // STOP listening & send
      if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null }
      speech.stopListening()
      handleVoiceSend()
    } else {
      // START listening
      if (!speech.isSupported) { toast.error('Speech recognition is not supported in this browser'); return }
      speech.startListening()
      setVoiceStatus('listening')
      setRecordingTime(0)
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000)
    }
  }, [voiceStatus, speech, handleVoiceSend])

  useEffect(() => {
    actionHandlerRef.current = new AIActionHandler(
      navigate,
      (callback) => setConfirmationDialog(callback)
    )
  }, [navigate])

  const isAuthenticated = () => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token')
    const user = sessionStorage.getItem('user') || localStorage.getItem('user')
    return !!(token && user)
  }

  // Get current user ID for user-specific storage
  const getCurrentUserId = () => {
    const userData = sessionStorage.getItem('user') || localStorage.getItem('user')
    if (userData) {
      try {
        return JSON.parse(userData).id
      } catch {
        return null
      }
    }
    return null
  }

  // Get current user role for role-based suggestions
  const getCurrentUserRole = (): string => {
    const userData = sessionStorage.getItem('user') || localStorage.getItem('user')
    if (userData) {
      try {
        const user = JSON.parse(userData)
        // Check for role in user object (could be 'role', 'user_role', or 'is_staff' for admin)
        if (user.role) return user.role.toLowerCase()
        if (user.user_role) return user.user_role.toLowerCase()
        if (user.is_superuser || user.is_staff) return 'admin'
        return 'student'
      } catch {
        return 'student'
      }
    }
    return 'student'
  }

  // Set user role on mount
  useEffect(() => {
    const role = getCurrentUserRole()
    setUserRole(role)
  }, [])

  const shouldShow = () => {
    const publicRoutes = ['/', '/login', '/register']
    return !publicRoutes.includes(location.pathname) && isAuthenticated()
  }

  // All hooks must be called before any early return!
  useEffect(() => {
    if (isOpen && sessions.length === 0) {
      loadConversations()
    }
  }, [isOpen])

  // Persist sessionId to user-specific storage
  // Note: Session messages are loaded via loadSessionMessagesRef to avoid temporal dead zone
  const loadSessionMessagesRef = useRef<((sid: string) => Promise<void>) | null>(null)

  useEffect(() => {
    if (sessionId) {
      const userId = getCurrentUserId()
      if (userId) {
        sessionStorage.setItem(`ai_mentor_session_id_${userId}`, sessionId)
        localStorage.setItem(`ai_mentor_session_id_${userId}`, sessionId)
      }
      // Call via ref to avoid temporal dead zone
      if (loadSessionMessagesRef.current) {
        loadSessionMessagesRef.current(sessionId)
      }
    }
  }, [sessionId])

  // Cleanup interval on unmount - MUST be before early return!
  useEffect(() => {
    return () => {
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current)
      }
    }
  }, [])

  // Cleanup idle timer on unmount - MUST be before early return!
  useEffect(() => {
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
      }
    }
  }, [])

  // Open the mentor from anywhere via window.dispatchEvent(new CustomEvent('open-ai-mentor')).
  // Single source of truth: the floating widget IS the AI mentor. (Loading of
  // conversations is handled by the isOpen effect above.)
  useEffect(() => {
    const openMentor = () => {
      if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null }
      setIsIdle(false)
      setIsOpen(true)
    }
    window.addEventListener('open-ai-mentor', openMentor)
    return () => window.removeEventListener('open-ai-mentor', openMentor)
  }, [])

  // Early return AFTER all hooks are called
  if (!shouldShow()) {
    return null
  }

  const loadConversations = async () => {
    try {
      const response = await aiAPI.getSessions()
      const sessionList = response.data.results || response.data || []
      setSessions(sessionList)

      if (sessionList.length > 0 && !sessionId) {
        const activeSession = sessionList.find((s: Session) => s.status === 'active') || sessionList[0]
        setSessionId(activeSession.id)
      }
    } catch (error) {
      console.error('Error loading conversations:', error)
    }
  }

  const loadSessionMessages = async (sid: string) => {
    try {
      setLoadingHistory(true)
      const response = await aiAPI.getSession(sid)
      const sessionMessages = response.data.messages || []

      const formattedMessages: Message[] = sessionMessages.map((msg: any) => ({
        role: msg.sender === 'user' ? 'user' : 'ai',
        content: msg.message
      }))

      // Check if this is the first message for auto-naming
      const hasUserMessages = sessionMessages.some((msg: any) => msg.sender === 'user')
      setIsFirstMessage(!hasUserMessages)

      if (formattedMessages.length === 0) {
        setMessages([])
        setLoadingHistory(false)
        setIsFirstMessage(true)
        streamAIResponse("Hi! I'm your CCIS-CodeHub AI Mentor. I can help you with:\n\n• Navigating the platform\n• Understanding features\n• Learning paths and modules\n• Projects and collaboration\n• Technical coding questions\n\nWhat would you like to know?")
        return
      }

      setMessages(formattedMessages)
    } catch (error: any) {
      console.error('Error loading messages:', error)
      // If session not found (404), clear the stored session and create new one
      if (error?.response?.status === 404) {
        const userId = getCurrentUserId()
        if (userId) {
          sessionStorage.removeItem(`ai_mentor_session_id_${userId}`)
          localStorage.removeItem(`ai_mentor_session_id_${userId}`)
        }
        setSessionId(null)
        setMessages([])
        setIsFirstMessage(true)
        // Auto-create a new session
        try {
          const response = await aiAPI.createSession({
            session_type: 'general_chat',
            project: null
          })
          const newSession = response.data
          setSessions(prev => [newSession, ...prev])
          setSessionId(newSession.id)
          streamAIResponse("Hi! I'm your CCIS-CodeHub AI Mentor. How can I help you today?")
        } catch (createError) {
          console.error('Error creating new session:', createError)
          streamAIResponse("Hi! I'm your AI Mentor. How can I help you today?")
        }
        return
      }
      setMessages([])
      setIsFirstMessage(true)
      streamAIResponse("Hi! I'm your AI Mentor. How can I help you today?")
    } finally {
      setLoadingHistory(false)
    }
  }

  // Assign function to ref after it's defined so useEffect can call it
  loadSessionMessagesRef.current = loadSessionMessages

  const createNewConversation = async () => {
    try {
      const response = await aiAPI.createSession({
        session_type: 'general_chat',
        project: null
      })
      const newSession = response.data
      setSessions(prev => [newSession, ...prev])
      setSessionId(newSession.id)
      setMessages([])
      setShowConversations(false)
      setIsFirstMessage(true)
      streamAIResponse("Hi! I'm your CCIS-CodeHub AI Mentor. I can help you with:\n\n• Navigating the platform\n• Understanding features\n• Learning paths and modules\n• Projects and collaboration\n• Technical coding questions\n\nWhat would you like to know?")
    } catch (error) {
      console.error('Error creating conversation:', error)
    }
  }

  // Delete conversation
  const deleteConversation = async (sid: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering switchConversation

    // Confirm before deleting
    if (!window.confirm('Delete this conversation?')) {
      return
    }


    try {
      await aiAPI.deleteSession(sid)

      // Remove from local state
      const updatedSessions = sessions.filter(s => s.id !== sid)
      setSessions(updatedSessions)

      // If we deleted the current session, clean up and switch
      if (sid === sessionId) {

        // Clear user-specific storage for this session
        const userId = getCurrentUserId()
        if (userId) {
          sessionStorage.removeItem(`ai_mentor_session_id_${userId}`)
          localStorage.removeItem(`ai_mentor_session_id_${userId}`)
        }

        setMessages([]) // Clear messages first

        if (updatedSessions.length > 0) {
          // Switch to the first available session
          const newSessionId = updatedSessions[0].id
          setSessionId(newSessionId)
        } else {
          // No sessions left, set to null first then create new
          setSessionId(null)
          // Use setTimeout to avoid state race condition
          setTimeout(() => {
            createNewConversation()
          }, 100)
        }
      }

      toast.success('Conversation deleted')
    } catch (error: any) {
      console.error('Error deleting conversation:', error)
      console.error('Error response:', error.response?.data)
      toast.error('Failed to delete conversation')
    }
  }

  // Auto-name conversation based on first query
  const autoNameConversation = async (userQuery: string) => {
    if (!sessionId) {
      return
    }
    if (!isFirstMessage) {
      return
    }

    // Generate a short title from the query (first 30 chars, cleaned up)
    let title = userQuery.trim()
    if (title.length > 35) {
      title = title.substring(0, 35).trim() + '...'
    }


    try {
      const response = await aiAPI.updateSession(sessionId, { title })
      // Update local state
      setSessions(prev => prev.map(s =>
        s.id === sessionId ? { ...s, title } : s
      ))
      setIsFirstMessage(false)
    } catch (error: any) {
      console.error('Error updating conversation title:', error)
      console.error('Error response:', error.response?.data)
    }
  }

  const switchConversation = (sid: string) => {
    setSessionId(sid)
    setShowConversations(false)
  }

  const handleToggle = async () => {
    // Clear any existing idle timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }

    const wasOpen = isOpen
    setIsOpen(!isOpen)
    setIsIdle(false) // Always exit idle when interacting

    if (!wasOpen) {
      // Opening the chat
      await loadConversations()
      if (!sessionId) {
        await createNewConversation()
      }
    } else {
      // Closing the chat - start idle timer (5 seconds)
      idleTimerRef.current = setTimeout(() => {
        setIsIdle(true)
      }, 5000)
    }
  }

  // Handle clicking the idle button to expand
  const handleIdleClick = () => {
    // Clear any pending idle timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
    setIsIdle(false)
    // Start new idle timer - if user doesn't open chat in 5 seconds, go back to idle
    idleTimerRef.current = setTimeout(() => {
      if (!isOpen) {
        setIsIdle(true)
      }
    }, 5000)
  }

  const stopStreaming = () => {
    shouldStopRef.current = true
    if (streamingIntervalRef.current) {
      clearInterval(streamingIntervalRef.current)
      streamingIntervalRef.current = null
    }

    if (streamingText) {
      setMessages(prev => [...prev, { role: 'ai', content: streamingText }])
    } else if (pendingStreamText) {
      setMessages(prev => [...prev, { role: 'ai', content: pendingStreamText }])
    }

    setStreamingText('')
    setPendingStreamText('')
    setIsStreaming(false)
    setLoading(false)
    shouldStopRef.current = false
  }

  const streamAIResponse = (text: string) => {
    setStreamingText('')
    setPendingStreamText(text)
    setIsStreaming(true)
    shouldStopRef.current = false

    let currentIndex = 0
    const speed = 5  // Fast streaming (was 20ms)

    if (streamingIntervalRef.current) {
      clearInterval(streamingIntervalRef.current)
    }

    streamingIntervalRef.current = setInterval(() => {
      if (shouldStopRef.current) {
        clearInterval(streamingIntervalRef.current!)
        setIsStreaming(false)
        setStreamingText('')
        setPendingStreamText('')
        return
      }

      if (currentIndex < text.length) {
        setStreamingText(text.substring(0, currentIndex + 1))
        currentIndex++
      } else {
        clearInterval(streamingIntervalRef.current!)
        setIsStreaming(false)
        setMessages(prev => [...prev, { role: 'ai', content: text }])
        setStreamingText('')
        setPendingStreamText('')
      }
    }, speed)
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isStreaming) return

    const userMessage: Message = { role: 'user', content: input }
    setMessages([...messages, userMessage])
    const userInput = input
    setInput('')
    setLoading(true)

    // Auto-name conversation on first message
    if (isFirstMessage && sessionId) {
      autoNameConversation(userInput)
    }

    try {
      if (!sessionId) {
        await createNewConversation()
        await new Promise(resolve => setTimeout(resolve, 500))
      }

      if (!sessionId) {
        throw new Error('Failed to create session')
      }

      const response = await aiAPI.sendMessage(sessionId, userInput, { current_page: location.pathname })
      const aiResponseText = response.data.ai_response?.message || response.data.response || "I'm here to help with CCIS-CodeHub!"

      setLoading(false)

      const action = response.data.action
      if (action && actionHandlerRef.current) {
        await actionHandlerRef.current.handleAction(action, sessionId)

        if (action.type === 'search_results' && action.results) {
          const actionButtons = generateSearchActionButtons(action.results, actionHandlerRef.current, sessionId)
          streamAIResponseWithResults(aiResponseText, action.results, actionButtons)
          return
        }
      }

      streamAIResponse(aiResponseText)

    } catch (error: any) {
      console.error('AI Chat error:', error)
      console.error('Error details:', error.response?.data)
      setLoading(false)

      // Show detailed error for debugging, not generic fallback
      let errorMessage = "Sorry, I couldn't process your request. "
      if (error.response?.status === 400) {
        errorMessage += error.response?.data?.error || "Please select an AI model in settings."
      } else if (error.response?.status === 500) {
        errorMessage += "Server error. Please try again."
      } else if (error.response?.data?.error) {
        errorMessage += error.response.data.error
      } else if (error.message) {
        errorMessage += error.message
      } else {
        errorMessage += "Please check your connection and try again."
      }

      streamAIResponse(errorMessage)
    }
  }

  const streamAIResponseWithResults = (text: string, results: SearchResult, buttons: ActionButton[]) => {
    setStreamingText('')
    setPendingStreamText(text)
    setIsStreaming(true)
    shouldStopRef.current = false

    let currentIndex = 0
    const speed = 5  // Fast streaming (was 20ms)

    if (streamingIntervalRef.current) {
      clearInterval(streamingIntervalRef.current)
    }

    streamingIntervalRef.current = setInterval(() => {
      if (shouldStopRef.current) {
        clearInterval(streamingIntervalRef.current!)
        setIsStreaming(false)
        setStreamingText('')
        setPendingStreamText('')
        return
      }

      if (currentIndex < text.length) {
        setStreamingText(text.substring(0, currentIndex + 1))
        currentIndex++
      } else {
        clearInterval(streamingIntervalRef.current!)
        setIsStreaming(false)
        setMessages(prev => [...prev, { role: 'ai', content: text, searchResults: results, actionButtons: buttons }])
        setStreamingText('')
        setPendingStreamText('')
      }
    }, speed)
  }

  // Role-based quick action suggestions (no emojis, just text)
  const getSuggestions = () => {
    const suggestions: Record<string, Array<{ label: string, value: string }>> = {
      student: [
        { label: "My Courses", value: "What courses am I enrolled in?" },
        { label: "My Progress", value: "Show my learning progress" },
        { label: "Recommend", value: "Recommend a course for me" },
        { label: "My Projects", value: "Show my projects" },
      ],
      instructor: [
        { label: "My Courses", value: "Show courses I teach" },
        { label: "Students", value: "Show my students' progress" },
        { label: "Create Module", value: "Help me create a new module" },
        { label: "Analytics", value: "Show course analytics" },
      ],
      admin: [
        { label: "Stats", value: "Show platform statistics" },
        { label: "Users", value: "Show user overview" },
        { label: "Courses", value: "List all courses" },
        { label: "Activity", value: "Show recent platform activity" },
      ]
    }
    return suggestions[userRole] || suggestions.student
  }

  const handleSuggestionClick = async (suggestionValue: string) => {
    setUsedSuggestions(prev => new Set([...prev, suggestionValue]))

    // Directly send the message (don't just fill the input)
    const userMessage: Message = { role: 'user', content: suggestionValue }
    setMessages(prev => [...prev, userMessage])
    setLoading(true)

    // Auto-name conversation on first message
    if (isFirstMessage && sessionId) {
      autoNameConversation(suggestionValue)
    }

    try {
      if (!sessionId) {
        await createNewConversation()
        await new Promise(resolve => setTimeout(resolve, 500))
      }

      if (!sessionId) {
        throw new Error('Failed to create session')
      }

      const response = await aiAPI.sendMessage(sessionId, suggestionValue, { current_page: location.pathname })
      const aiResponseText = response.data.ai_response?.message || response.data.response || "I'm here to help with CCIS-CodeHub!"

      setLoading(false)

      const action = response.data.action
      if (action && actionHandlerRef.current) {
        await actionHandlerRef.current.handleAction(action, sessionId)

        if (action.type === 'search_results' && action.results) {
          const actionButtons = generateSearchActionButtons(action.results, actionHandlerRef.current, sessionId)
          streamAIResponseWithResults(aiResponseText, action.results, actionButtons)
          return
        }
      }

      streamAIResponse(aiResponseText)

    } catch (error: any) {
      console.error('AI Chat error:', error)
      setLoading(false)
      let errorMessage = "Sorry, I couldn't process your request. "
      if (error.response?.status === 400) {
        errorMessage += error.response?.data?.error || "Please select an AI model in settings."
      } else if (error.response?.data?.error) {
        errorMessage += error.response.data.error
      } else {
        errorMessage += "Please try again."
      }
      streamAIResponse(errorMessage)
    }
  }

  // Get display title for session
  const getSessionTitle = (session: Session) => {
    if (session.title) return session.title
    return session.session_type.replace('_', ' ')
  }

  return (
    <>
      {/* Floating Button - Idle (Minimized Side Dock) or Active (Full Button) */}
      {!isOpen && isIdle && (
        <button
          onClick={handleIdleClick}
          // The idle dock: a slim tab on the right edge that widens and comes
          // up to full opacity on hover. Staying out of the way until it is
          // wanted is the point of the idle state.
          //
          // z-40, not the z-50 it used to carry. A persistent launcher belongs
          // on the page-chrome layer with the nav (DESIGN_SYSTEM.md §6); at
          // z-50 it sat on the modal layer and drew over dialogs — the reactors
          // sheet, the comments modal, the channel drawer and thread pane.
          className="fixed right-0 bottom-1/3 w-10 h-16 bg-slate-900/60 backdrop-blur-sm
            border border-slate-700/30 border-r-0 rounded-l-xl shadow-lg hover:w-12
            hover:bg-slate-800/80 transition-all duration-300 z-40 flex items-center
            justify-center group"
          title="Open AI Mentor"
        >
          {/* The Bot glyph, not the 🤖 emoji: it matches the chat dock above it
              and renders the same on every platform. */}
          <Bot className="w-4 h-4 text-purple-400 opacity-60 group-hover:opacity-100 group-hover:w-5 group-hover:h-5 transition-all" />
        </button>
      )}

      {/* Floating Button - Active (Full floating button with Lucide Icon) */}
      {!isOpen && !isIdle && (
        <button
          onClick={handleToggle}
          className="fixed bottom-24 right-4 sm:bottom-6 sm:right-6 w-12 h-12 sm:w-14 sm:h-14 bg-purple-600 hover:bg-purple-500 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 z-40 flex items-center justify-center"
        >
          <Bot className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
        </button>
      )}

      {/* Chat Window - Dark Glass UI (full screen in voice mode) */}
      {isOpen && (
        <div className={isVoiceMode
          ? "fixed inset-0 bg-neutral-950/98 backdrop-blur-xl z-[60] flex flex-col overflow-hidden"
          : "fixed inset-2 bottom-20 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[420px] sm:h-[550px] bg-neutral-900/95 backdrop-blur-lg border border-neutral-700/50 rounded-2xl shadow-2xl z-[60] flex flex-col overflow-hidden"
        }>
          {/* Header - Dark Glass */}
          <div className="bg-neutral-800/80 backdrop-blur-lg border-b border-neutral-700/50 p-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowConversations(!showConversations)}
                className="text-neutral-400 hover:text-purple-400 hover:bg-neutral-700/50 rounded-lg p-1.5 flex items-center justify-center transition-colors"
                title="Conversations"
              >
                <Menu className="w-5 h-5" />
              </button>
              <Bot className="w-7 h-7 text-purple-400" />
              <div>
                <h3 className="font-bold text-white">AI Mentor</h3>
                <p className="text-xs text-neutral-400">Smarter Than Your Average Mentor</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Voice mode is COMING SOON — the backend returns 503 until
                  ENABLE_VOICE_FEATURES and a TTS key are configured, so the
                  toggle is disabled rather than failing mid-conversation. */}
              {voiceEnabled ? (
                <button
                  onClick={() => { setIsVoiceMode(!isVoiceMode); if (isVoiceMode && speech.isListening) speech.stopListening(); }}
                  className={`hover:bg-neutral-700/50 rounded-lg p-2 flex items-center justify-center transition-colors ${
                    isVoiceMode ? 'text-purple-400 bg-purple-500/10' : 'text-neutral-400 hover:text-white'
                  }`}
                  title={isVoiceMode ? 'Switch to chat' : 'Switch to voice'}
                >
                  {isVoiceMode ? <MessageSquare className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>
              ) : (
                <span
                  className="relative rounded-lg p-2 flex items-center justify-center text-neutral-600 cursor-not-allowed"
                  title="Voice chat is coming soon"
                  aria-label="Voice chat — coming soon"
                >
                  <Mic className="w-5 h-5" />
                  <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-400/80" />
                </span>
              )}
              <button
                onClick={createNewConversation}
                className="text-neutral-400 hover:text-white hover:bg-neutral-700/50 rounded-lg p-2 flex items-center justify-center transition-colors"
                title="New Chat"
              >
                <Plus className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="text-neutral-400 hover:text-white hover:bg-neutral-700/50 rounded-lg p-2 flex items-center justify-center transition-colors"
                title="Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={handleToggle}
                className="text-neutral-400 hover:text-white hover:bg-neutral-700/50 rounded-lg p-2 flex items-center justify-center transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Conversation List Sidebar - Dark Glass */}
          {showConversations && (
            <div className="absolute left-0 top-0 h-full w-64 bg-neutral-900/98 backdrop-blur-lg border-r border-neutral-700/50 rounded-l-2xl z-10 overflow-hidden shadow-xl">
              <div className="p-4 bg-neutral-800/80 border-b border-neutral-700/50 flex items-center justify-between">
                <h3 className="font-bold text-white">Conversations</h3>
                <button
                  onClick={() => setShowConversations(false)}
                  className="text-neutral-400 hover:text-white hover:bg-neutral-700/50 rounded-lg p-1.5 transition-colors"
                  title="Close panel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="overflow-y-auto h-[calc(100%-4rem)]">
                {sessions.length === 0 ? (
                  <div className="p-4 text-center text-neutral-400">
                    <p className="text-sm">No conversations yet</p>
                    <button onClick={createNewConversation} className="mt-2 px-4 py-2 bg-purple-600 rounded-lg text-white text-sm hover:bg-purple-700">
                      Start New Chat
                    </button>
                  </div>
                ) : (
                  <div className="p-2 space-y-2">
                    {sessions.map((session) => (
                      <div
                        key={session.id}
                        className={`w-full text-left p-3 rounded-lg transition-colors cursor-pointer group relative ${session.id === sessionId ? 'bg-purple-600 text-white' : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'}`}
                        onClick={() => switchConversation(session.id)}
                      >
                        <div className="text-sm font-medium truncate pr-6">{getSessionTitle(session)}</div>
                        <div className="text-xs opacity-70 mt-1">{new Date(session.started_at).toLocaleDateString()}</div>

                        {/* Delete button with Lucide icon */}
                        <button
                          onClick={(e) => deleteConversation(session.id, e)}
                          className={`absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ${session.id === sessionId ? 'hover:bg-purple-700 text-white' : 'hover:bg-red-600/80 text-neutral-400 hover:text-white'}`}
                          title="Delete conversation"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── VOICE MODE (Full Screen) ───────────────────────── */}
          {isVoiceMode ? (
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden bg-neutral-950">
              {/* Voice Panel (center — takes up most space) */}
              <div className="flex-1 flex flex-col items-center justify-center relative min-h-0">
                {/* Animated background glow */}
                <div className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-all duration-700 ${
                  voiceStatus === 'speaking' ? 'opacity-100 scale-100' : voiceStatus === 'listening' ? 'opacity-60 scale-90' : 'opacity-0 scale-75'
                }`}>
                  <div className={`w-[600px] h-[600px] rounded-full blur-[120px] transition-colors duration-500 ${
                    voiceStatus === 'listening' ? 'bg-red-600/15' : 'bg-purple-600/12'
                  }`} />
                </div>

                {/* Status indicator */}
                <div className={`text-sm font-medium mb-6 h-6 flex items-center gap-2 transition-all duration-300 ${
                  voiceStatus === 'listening' ? 'text-red-400' : voiceStatus === 'processing' ? 'text-amber-400 animate-pulse' : voiceStatus === 'speaking' ? 'text-purple-400' : 'text-neutral-600'
                }`}>
                  {voiceStatus === 'listening' && <><span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" /> Listening...</>}
                  {voiceStatus === 'processing' && <><span className="w-2 h-2 rounded-full bg-amber-400" /> Processing...</>}
                  {voiceStatus === 'speaking' && <><span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" /> AI Speaking...</>}
                </div>

                {/* Title with subtitle */}
                <h2 className="text-white text-3xl md:text-4xl font-bold mb-2 tracking-tight relative z-10">AI Mentor</h2>
                <p className="text-neutral-500 text-sm mb-12 relative z-10">Voice Assistant</p>

                {/* Waveform — large, centered */}
                <div className="mb-12 w-full max-w-[750px] px-6 relative z-10">
                  <WaveformVisualizer
                    analyserNode={audio.analyserNode}
                    isActive={voiceStatus === 'speaking' || voiceStatus === 'listening'}
                    width={700}
                    height={140}
                    color={voiceStatus === 'listening' ? '#f87171' : '#a78bfa'}
                    secondaryColor={voiceStatus === 'listening' ? '#dc2626' : '#7c3aed'}
                  />
                </div>

                {/* Voice Toggle Button — Click to start/stop */}
                <button
                  onClick={handleVoiceToggle}
                  disabled={voiceStatus === 'processing' || voiceStatus === 'speaking'}
                  className={`relative z-10 w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center transition-all duration-300 ${
                    voiceStatus === 'listening'
                      ? 'bg-red-500 scale-110 shadow-[0_0_40px_rgba(239,68,68,0.5)] ring-4 ring-red-500/30 animate-pulse'
                      : voiceStatus === 'processing'
                        ? 'bg-neutral-700 cursor-wait'
                        : voiceStatus === 'speaking'
                          ? 'bg-purple-500/30 cursor-not-allowed ring-4 ring-purple-500/20 shadow-[0_0_30px_rgba(168,85,247,0.3)]'
                          : 'bg-purple-600 hover:bg-purple-500 hover:scale-105 shadow-[0_0_30px_rgba(147,51,234,0.3)] hover:shadow-[0_0_50px_rgba(147,51,234,0.5)]'
                  }`}
                >
                  {voiceStatus === 'listening'
                    ? <MicOff className="w-8 h-8 md:w-10 md:h-10 text-white" />
                    : voiceStatus === 'processing'
                      ? <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : <Mic className="w-8 h-8 md:w-10 md:h-10 text-white" />
                  }
                </button>
                <p className="text-neutral-500 text-sm mt-5 select-none relative z-10">
                  {voiceStatus === 'listening' ? 'Click to send' : voiceStatus === 'processing' ? 'Thinking...' : voiceStatus === 'speaking' ? 'Playing response...' : 'Click to start'}
                </p>

                {/* Timer */}
                {voiceStatus === 'listening' && (
                  <p className="text-red-400/80 text-base mt-3 font-mono relative z-10">
                    {String(Math.floor(recordingTime / 60)).padStart(2, '0')}:{String(recordingTime % 60).padStart(2, '0')}
                  </p>
                )}

                {/* Interim transcript preview */}
                {speech.interimTranscript && (
                  <p className="text-purple-300/50 text-sm mt-5 italic max-w-lg text-center relative z-10">
                    &ldquo;{speech.interimTranscript}&rdquo;
                  </p>
                )}
              </div>

              {/* Transcript Panel */}
              <div className="md:w-[300px] h-48 md:h-auto border-t md:border-t-0 md:border-l border-neutral-800 overflow-y-auto p-6 bg-neutral-900/80">
                <h4 className="text-neutral-500 text-[11px] font-semibold mb-5 uppercase tracking-[0.2em]">Transcript</h4>
                {voiceTranscript.length === 0 ? (
                  <div className="flex flex-col items-center justify-center mt-12 opacity-40">
                    <Mic className="w-8 h-8 text-neutral-600 mb-3" />
                    <p className="text-neutral-600 text-sm text-center">Hold the mic button<br/>and start speaking</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {voiceTranscript.map((entry, i) => (
                      <div key={i} className={`p-3 rounded-lg ${entry.role === 'user' ? 'bg-neutral-800/50' : 'bg-purple-500/10 border border-purple-500/20'}`}>
                        <span className={`text-[10px] font-bold uppercase tracking-widest ${
                          entry.role === 'user' ? 'text-neutral-500' : 'text-purple-500'
                        }`}>{entry.role === 'user' ? 'You' : 'AI Mentor'}</span>
                        <p className={`text-sm mt-1 leading-relaxed ${
                          entry.role === 'user' ? 'text-neutral-300' : 'text-purple-200'
                        }`}>{entry.text}</p>
                      </div>
                    ))}
                    <div ref={transcriptEndRef} />
                  </div>
                )}
              </div>
            </div>
          ) : (
          /* ── CHAT MODE (existing) ───────────────────────────── */
          <>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 relative">
            {loadingHistory ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="flex gap-1 justify-center mb-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                  <p className="text-sm text-neutral-400">Loading conversation...</p>
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] ${msg.role === 'user' ? 'bg-purple-600 text-white' : 'bg-neutral-800/80 backdrop-blur-sm border border-neutral-700/50 text-neutral-100'} rounded-2xl overflow-hidden shadow-md`}>
                      <div className="p-3 text-sm">
                        {msg.role === 'ai' ? (
                          <FormattedMessage content={msg.content} />
                        ) : (
                          <span className="whitespace-pre-wrap">{msg.content}</span>
                        )}
                      </div>

                      {msg.searchResults && (
                        <div className="border-t border-neutral-700 p-3 space-y-2">
                          <div className="text-xs font-semibold text-purple-400 mb-2 flex items-center gap-1.5">
                            <Search className="w-3.5 h-3.5" />
                            Found {msg.searchResults.total} results
                          </div>
                          {msg.searchResults.paths && msg.searchResults.paths.length > 0 && (
                            <div className="space-y-1">
                              {msg.searchResults.paths.slice(0, 3).map((path) => (
                                <div key={path.id} className="bg-neutral-700/50 p-2 rounded text-xs">
                                  <div className="font-medium flex items-center gap-1.5">
                                    <BookOpen className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                                    <span>{path.name}</span>
                                  </div>
                                  <div className="text-neutral-400 text-[10px] mt-1">{path.module_count} modules</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {msg.searchResults.modules && msg.searchResults.modules.length > 0 && (
                            <div className="space-y-1">
                              {msg.searchResults.modules.slice(0, 2).map((module) => (
                                <div key={module.id} className="bg-neutral-700/50 p-2 rounded text-xs">
                                  <div className="font-medium flex items-center gap-1.5">
                                    <BookOpen className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                                    <span>{module.title}</span>
                                  </div>
                                  <div className="text-neutral-400 text-[10px] mt-1">{module.path_name}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {msg.actionButtons && msg.actionButtons.length > 0 && (
                        <div className="border-t border-neutral-700 p-3 space-y-2">
                          {msg.actionButtons.map((btn, btnIdx) => (
                            <button
                              key={btnIdx}
                              onClick={btn.onClick}
                              className={`w-full text-xs px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${btn.variant === 'primary' ? 'bg-purple-600 hover:bg-purple-700 text-white' : btn.variant === 'success' ? 'bg-green-600 hover:bg-green-700 text-white' : btn.variant === 'danger' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-neutral-700 hover:bg-neutral-600 text-white'}`}
                            >
                              {btn.icon && <span>{btn.icon}</span>}
                              <span>{btn.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}

            {isStreaming && streamingText && (
              <div className="flex justify-start">
                <div className="max-w-[85%] p-3 rounded-2xl bg-neutral-800/80 backdrop-blur-sm border border-neutral-700/50 text-neutral-100 shadow-md">
                  <span className="whitespace-pre-wrap text-sm leading-relaxed">{streamingText}</span>
                  <span className="inline-block w-0.5 h-4 ml-1 bg-purple-400 animate-pulse"></span>
                </div>
              </div>
            )}

            {loading && !isStreaming && (
              <div className="flex justify-start">
                <div className="bg-neutral-800/80 backdrop-blur-sm border border-neutral-700/50 p-3 rounded-2xl shadow-md">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                    <span className="text-sm text-neutral-400">AI is thinking...</span>
                  </div>
                </div>
              </div>
            )}

            {isStreaming && (
              <div className="flex justify-center my-2">
                <button onClick={stopStreaming} className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-neutral-200 text-xs rounded-lg transition-colors flex items-center gap-2">
                  <Square className="w-3 h-3 fill-current" /><span>Stop Generating</span>
                </button>
              </div>
            )}
          </div>

          {/* Quick Suggestion Buttons - Absolutely positioned overlay above input */}
          <div className={`absolute bottom-16 right-4 z-20 transition-all duration-300 pointer-events-auto ${input.length > 0 || loading || isStreaming ? 'opacity-0 pointer-events-none translate-y-2' : 'opacity-100 translate-y-0'}`}>
            <div className="flex flex-col gap-2 items-end">
              {getSuggestions()
                .filter(s => !usedSuggestions.has(s.value))
                .slice(0, 4)
                .map((suggestion, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion.value)}
                    className="px-3 py-1.5 text-xs text-neutral-300 hover:text-white border border-neutral-500/50 hover:border-purple-400 rounded-full transition-all flex items-center gap-1 group bg-neutral-900/90 backdrop-blur-sm hover:bg-purple-600/80 shadow-md"
                  >
                    <span>{suggestion.label}</span>
                    <ChevronRight className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
            </div>
          </div>

          {/* Input */}
          <form onSubmit={handleSendMessage} className="p-3 sm:p-4 border-t border-neutral-700/50 bg-neutral-900/50">
            <div className="flex space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask anything..."
                className="flex-1 px-4 py-2.5 bg-neutral-800/50 backdrop-blur-sm border border-neutral-700/50 rounded-xl focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500/50 text-sm placeholder:text-neutral-500 transition-all"
              />
              <button
                type="submit"
                disabled={loading || isStreaming}
                className="px-4 py-2.5 bg-purple-600 hover:bg-purple-500 rounded-xl transition disabled:opacity-50 shadow-lg shadow-purple-500/20"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-white">
                  <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                </svg>
              </button>
            </div>
          </form>
          </>
          )}
        </div>
      )}

      <AIChatSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onModelChange={() => setShowSettings(false)}
      />

      {confirmationDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="shrink-0 w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Confirm Action</h3>
                <p className="text-neutral-300 text-sm">{confirmationDialog.message}</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { confirmationDialog.onCancel?.(); setConfirmationDialog(null) }}
                className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 rounded-lg transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={async () => { await confirmationDialog.onConfirm(); setConfirmationDialog(null) }}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-sm font-medium"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
